import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, ChevronDown, FileText, RefreshCw, Save, Send } from 'lucide-react';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';

const pageKeys = ['homepage', 'pricing', 'live', 'weekly_haircuts', 'colormetrics', 'video_library_pass', 'mentorship'];

const emptyForm = {
  id: null,
  page_key: 'homepage',
  locale: 'el',
  status: 'draft',
  seo_title: '',
  seo_description: '',
  og_title: '',
  og_description: '',
  sections: {}
};

const defaultSections = {
  homepage: {
    hero: {
      kicker: 'MISEL GROUP DIGITAL LAB',
      headline: '',
      title: '',
      subheadline: '',
      subtitle: '',
      body: '',
      description: '',
      primary_cta_label: '',
      primary_cta_url: '/register',
      secondary_cta_label: '',
      secondary_cta_url: '/pricing'
    },
    positioning: { title: '', body: '' },
    programs: { title: '', items: [] }
  },
  pricing: {
    hero: { title: '', subtitle: '', description: '' },
    plans_intro: { title: '', body: '' },
    plan_copy: {},
    per_video: ''
  },
  live: {
    hero: { kicker: '', title: '', subtitle: '' },
    sections: {
      live_now_title: '',
      upcoming_title: '',
      replays_title: '',
      empty_title: '',
      empty_body: '',
      no_live_now: '',
      no_upcoming: '',
      no_replays: '',
      load_error: ''
    },
    buttons: {
      watch_live: '',
      watch_replay: '',
      view_details: ''
    },
    labels: {
      one_time_access: '',
      includes_replay: '',
      date_tba: '',
      ends: ''
    }
  }
};

const clone = (value) => JSON.parse(JSON.stringify(value || {}));
const jsonText = (value) => JSON.stringify(value || {}, null, 2);

const Field = ({ label, children }) => (
  <label className="block min-w-0">
    <span className="text-xs text-gray-500">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const ContentManagement = () => {
  const { token } = useAuth();
  const [pages, setPages] = useState([]);
  const [selectedKey, setSelectedKey] = useState('homepage');
  const [selectedLocale, setSelectedLocale] = useState('el');
  const [form, setForm] = useState(emptyForm);
  const [sections, setSections] = useState(defaultSections.homepage);
  const [sectionsText, setSectionsText] = useState(jsonText(defaultSections.homepage));
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [jsonError, setJsonError] = useState('');
  const [missingTranslation, setMissingTranslation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const syncSections = (next) => {
    setSections(next);
    setSectionsText(jsonText(next));
    setJsonError('');
  };

  const setPath = (path, value) => {
    const next = clone(sections);
    let cursor = next;
    path.slice(0, -1).forEach((key) => {
      if (!cursor[key] || typeof cursor[key] !== 'object') cursor[key] = {};
      cursor = cursor[key];
    });
    cursor[path[path.length - 1]] = value;
    syncSections(next);
  };

  const setManyPaths = (entries) => {
    const next = clone(sections);
    entries.forEach(([path, value]) => {
      let cursor = next;
      path.slice(0, -1).forEach((key) => {
        if (!cursor[key] || typeof cursor[key] !== 'object') cursor[key] = {};
        cursor = cursor[key];
      });
      cursor[path[path.length - 1]] = value;
    });
    syncSections(next);
  };

  const loadList = useCallback(async () => {
    const res = await axios.get(`${API}/admin/content/pages`, { headers });
    setPages(res.data || []);
  }, [headers]);

  const loadPage = useCallback(async (pageKey = selectedKey, locale = selectedLocale) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/content/pages/${pageKey}?locale=${locale}`, { headers });
      const page = res.data?.page;
      setMissingTranslation(Boolean(res.data?.missing_translation));
      const baseSections = defaultSections[pageKey] || {};
      const loadedSections = page ? { ...clone(baseSections), ...clone(page.sections || {}) } : clone(baseSections);
      setForm(page ? { ...emptyForm, ...page } : { ...emptyForm, page_key: pageKey, locale, sections: loadedSections });
      syncSections(loadedSections);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [headers, selectedKey, selectedLocale]);

  useEffect(() => {
    loadList().catch(() => {});
  }, [loadList]);

  useEffect(() => {
    loadPage(selectedKey, selectedLocale);
  }, [selectedKey, selectedLocale, loadPage]);

  const parseSectionsText = () => {
    try {
      const parsed = JSON.parse(sectionsText || '{}');
      if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
        throw new Error('Sections must be a JSON object');
      }
      setJsonError('');
      return parsed;
    } catch (e) {
      const message = e.message || 'Invalid sections JSON';
      setJsonError(message);
      toast.error(message);
      return null;
    }
  };

  const applyJsonToForm = () => {
    const parsed = parseSectionsText();
    if (!parsed) return;
    setSections(parsed);
    toast.success('Advanced JSON applied');
  };

  const payload = (status = form.status || 'draft') => {
    const parsed = parseSectionsText();
    if (!parsed) return null;
    return {
      page_key: selectedKey,
      locale: selectedLocale,
      status,
      seo_title: form.seo_title || '',
      seo_description: form.seo_description || '',
      og_title: form.og_title || '',
      og_description: form.og_description || '',
      sections: parsed
    };
  };

  const saveDraft = async () => {
    const data = payload('draft');
    if (!data) return null;
    setSaving(true);
    try {
      const res = form.id
        ? await axios.put(`${API}/admin/content/pages/${form.id}`, data, { headers })
        : await axios.post(`${API}/admin/content/pages`, data, { headers });
      setForm({ ...emptyForm, ...res.data });
      syncSections(res.data.sections || {});
      toast.success('Draft saved');
      await loadList();
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    let pageId = form.id;
    if (!pageId) {
      const saved = await saveDraft();
      pageId = saved?.id;
    }
    if (!pageId) {
      toast.info('Save the draft first, then publish');
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/content/pages/${pageId}/publish`, {}, { headers });
      setForm({ ...emptyForm, ...res.data });
      syncSections(res.data.sections || {});
      toast.success('Published');
      await loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    }
  };

  const archivePage = async () => {
    if (!form.id) return;
    try {
      await axios.post(`${API}/admin/content/pages/${form.id}/archive`, {}, { headers });
      toast.success('Archived');
      await loadList();
      await loadPage(selectedKey, selectedLocale);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    }
  };

  const seedDefaults = async () => {
    try {
      const res = await axios.post(`${API}/admin/content/pages/seed-defaults`, {}, { headers });
      toast.success(`Seed complete: ${res.data.created_count || 0} created, ${res.data.updated_count || 0} updated`);
      await loadList();
      await loadPage(selectedKey, selectedLocale);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seed failed');
    }
  };

  const hero = sections.hero || {};
  const positioning = sections.positioning || {};
  const programs = sections.programs || {};
  const pricingHero = sections.hero || {};
  const plansIntro = sections.plans_intro || {};
  const liveHero = sections.hero || {};
  const liveSections = sections.sections || {};
  const liveButtons = sections.buttons || {};
  const liveLabels = sections.labels || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="font-roboto font-bold text-xl lg:text-2xl text-white flex items-center gap-2">
            <FileText className="text-primary" size={22} />
            Content
          </h1>
          <p className="text-sm text-gray-500 mt-1">Edit public page copy without code or a deploy.</p>
        </div>
        <Button variant="outline" className="border-white/20 text-white" onClick={seedDefaults}>
          <RefreshCw size={16} className="mr-2" />
          Seed defaults
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-4">
        <div className="bg-misel-card border border-white/10 rounded-sm p-4 space-y-4">
          <Field label="Page">
            <Select value={selectedKey} onValueChange={setSelectedKey}>
              <SelectTrigger className="bg-black/30 border-white/15 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="z-[9999] bg-[#111111] border-white/15 text-white">
                {pageKeys.map((key) => <SelectItem key={key} value={key}>{key}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Locale">
            <Select value={selectedLocale} onValueChange={setSelectedLocale}>
              <SelectTrigger className="bg-black/30 border-white/15 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="z-[9999] bg-[#111111] border-white/15 text-white">
                <SelectItem value="el">EL</SelectItem>
                <SelectItem value="en">EN</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {missingTranslation && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 rounded-sm p-3 text-sm">
              Missing translation for this locale.
            </div>
          )}
          <div className="space-y-2 text-xs text-gray-400">
            <p>Status: <span className="text-white">{form.status || 'draft'}</span></p>
            <p>Updated: <span className="text-white">{form.updated_at ? form.updated_at.slice(0, 16) : '-'}</span></p>
            <p>Published: <span className="text-white">{form.published_at ? form.published_at.slice(0, 16) : '-'}</span></p>
          </div>
          <div className="max-h-64 overflow-y-auto border-t border-white/10 pt-3 space-y-2">
            {pages.map((page) => (
              <button
                key={page.id}
                type="button"
                onClick={() => { setSelectedKey(page.page_key); setSelectedLocale(page.locale); }}
                className="w-full text-left text-xs bg-black/20 hover:bg-white/5 rounded-sm p-2 text-gray-300 break-words"
              >
                <span className="block text-white">{page.page_key} / {page.locale}</span>
                <span>{page.status}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-misel-card border border-white/10 rounded-sm p-4 sm:p-6 space-y-6 min-w-0">
          {loading ? (
            <div className="h-80 bg-black/20 animate-pulse rounded-sm" />
          ) : (
            <>
              <section className="space-y-3">
                <h2 className="text-white font-semibold">SEO</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="SEO title">
                    <Input value={form.seo_title || ''} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className="bg-black/30 border-white/15 text-white" />
                  </Field>
                  <Field label="OG title">
                    <Input value={form.og_title || ''} onChange={(e) => setForm({ ...form, og_title: e.target.value })} className="bg-black/30 border-white/15 text-white" />
                  </Field>
                  <Field label="SEO description">
                    <Textarea value={form.seo_description || ''} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className="bg-black/30 border-white/15 text-white min-h-20" />
                  </Field>
                  <Field label="OG description">
                    <Textarea value={form.og_description || ''} onChange={(e) => setForm({ ...form, og_description: e.target.value })} className="bg-black/30 border-white/15 text-white min-h-20" />
                  </Field>
                </div>
              </section>

              {selectedKey === 'homepage' && (
                <section className="space-y-5">
                  <h2 className="text-white font-semibold">Homepage copy</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Hero kicker">
                      <Input value={hero.kicker || ''} onChange={(e) => setPath(['hero', 'kicker'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Hero headline">
                      <Input value={hero.headline || hero.title || ''} onChange={(e) => setManyPaths([[['hero', 'headline'], e.target.value], [['hero', 'title'], e.target.value]])} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Hero subheadline">
                      <Textarea value={hero.subheadline || hero.subtitle || ''} onChange={(e) => setManyPaths([[['hero', 'subheadline'], e.target.value], [['hero', 'subtitle'], e.target.value]])} className="bg-black/30 border-white/15 text-white min-h-24" />
                    </Field>
                    <Field label="Hero body / description">
                      <Textarea value={hero.body || hero.description || ''} onChange={(e) => setManyPaths([[['hero', 'body'], e.target.value], [['hero', 'description'], e.target.value]])} className="bg-black/30 border-white/15 text-white min-h-24" />
                    </Field>
                    <Field label="Primary CTA label">
                      <Input value={hero.primary_cta_label || ''} onChange={(e) => setPath(['hero', 'primary_cta_label'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Primary CTA URL">
                      <Input value={hero.primary_cta_url || ''} onChange={(e) => setPath(['hero', 'primary_cta_url'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Secondary CTA label">
                      <Input value={hero.secondary_cta_label || ''} onChange={(e) => setPath(['hero', 'secondary_cta_label'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Secondary CTA URL">
                      <Input value={hero.secondary_cta_url || ''} onChange={(e) => setPath(['hero', 'secondary_cta_url'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Positioning title">
                      <Input value={positioning.title || ''} onChange={(e) => setPath(['positioning', 'title'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Programs title">
                      <Input value={programs.title || ''} onChange={(e) => setPath(['programs', 'title'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                  </div>
                  <Field label="Positioning body">
                    <Textarea value={positioning.body || ''} onChange={(e) => setPath(['positioning', 'body'], e.target.value)} className="bg-black/30 border-white/15 text-white min-h-24" />
                  </Field>
                  <Field label="Program cards, one per line as Title | Description">
                    <Textarea
                      value={(programs.items || []).map((item) => `${item.title || ''} | ${item.body || item.description || ''}`).join('\n')}
                      onChange={(e) => setPath(['programs', 'items'], e.target.value.split('\n').filter(Boolean).map((line) => {
                        const [title, ...body] = line.split('|');
                        return { title: title.trim(), body: body.join('|').trim() };
                      }))}
                      className="bg-black/30 border-white/15 text-white min-h-28"
                    />
                  </Field>
                </section>
              )}

              {selectedKey === 'pricing' && (
                <section className="space-y-5">
                  <h2 className="text-white font-semibold">Pricing copy</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Hero title">
                      <Input value={pricingHero.title || ''} onChange={(e) => setPath(['hero', 'title'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Hero subtitle">
                      <Textarea value={pricingHero.subtitle || pricingHero.description || ''} onChange={(e) => setManyPaths([[['hero', 'subtitle'], e.target.value], [['hero', 'description'], e.target.value]])} className="bg-black/30 border-white/15 text-white min-h-24" />
                    </Field>
                    <Field label="Plans intro title">
                      <Input value={plansIntro.title || ''} onChange={(e) => setPath(['plans_intro', 'title'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Plans intro body">
                      <Textarea value={plansIntro.body || ''} onChange={(e) => setPath(['plans_intro', 'body'], e.target.value)} className="bg-black/30 border-white/15 text-white min-h-24" />
                    </Field>
                  </div>
                </section>
              )}

              {selectedKey === 'live' && (
                <section className="space-y-5">
                  <h2 className="text-white font-semibold">Live page copy</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Hero kicker">
                      <Input value={liveHero.kicker || ''} onChange={(e) => setPath(['hero', 'kicker'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Hero title">
                      <Textarea value={liveHero.title || ''} onChange={(e) => setPath(['hero', 'title'], e.target.value)} className="bg-black/30 border-white/15 text-white min-h-24" />
                    </Field>
                    <Field label="Hero subtitle">
                      <Textarea value={liveHero.subtitle || ''} onChange={(e) => setPath(['hero', 'subtitle'], e.target.value)} className="bg-black/30 border-white/15 text-white min-h-24" />
                    </Field>
                    <Field label="Live Now section title">
                      <Input value={liveSections.live_now_title || ''} onChange={(e) => setPath(['sections', 'live_now_title'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Upcoming section title">
                      <Input value={liveSections.upcoming_title || ''} onChange={(e) => setPath(['sections', 'upcoming_title'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Replays section title">
                      <Input value={liveSections.replays_title || ''} onChange={(e) => setPath(['sections', 'replays_title'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Empty state title">
                      <Input value={liveSections.empty_title || ''} onChange={(e) => setPath(['sections', 'empty_title'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Empty state body">
                      <Textarea value={liveSections.empty_body || ''} onChange={(e) => setPath(['sections', 'empty_body'], e.target.value)} className="bg-black/30 border-white/15 text-white min-h-24" />
                    </Field>
                    <Field label="No live now text">
                      <Input value={liveSections.no_live_now || ''} onChange={(e) => setPath(['sections', 'no_live_now'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="No upcoming text">
                      <Input value={liveSections.no_upcoming || ''} onChange={(e) => setPath(['sections', 'no_upcoming'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="No replays text">
                      <Input value={liveSections.no_replays || ''} onChange={(e) => setPath(['sections', 'no_replays'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Load error text">
                      <Textarea value={liveSections.load_error || ''} onChange={(e) => setPath(['sections', 'load_error'], e.target.value)} className="bg-black/30 border-white/15 text-white min-h-20" />
                    </Field>
                    <Field label="Watch live button">
                      <Input value={liveButtons.watch_live || ''} onChange={(e) => setPath(['buttons', 'watch_live'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Watch replay button">
                      <Input value={liveButtons.watch_replay || ''} onChange={(e) => setPath(['buttons', 'watch_replay'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="View details button">
                      <Input value={liveButtons.view_details || ''} onChange={(e) => setPath(['buttons', 'view_details'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="One-time access label">
                      <Input value={liveLabels.one_time_access || ''} onChange={(e) => setPath(['labels', 'one_time_access'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Includes replay label">
                      <Textarea value={liveLabels.includes_replay || ''} onChange={(e) => setPath(['labels', 'includes_replay'], e.target.value)} className="bg-black/30 border-white/15 text-white min-h-20" />
                    </Field>
                    <Field label="Date TBA label">
                      <Input value={liveLabels.date_tba || ''} onChange={(e) => setPath(['labels', 'date_tba'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                    <Field label="Ends label">
                      <Input value={liveLabels.ends || ''} onChange={(e) => setPath(['labels', 'ends'], e.target.value)} className="bg-black/30 border-white/15 text-white" />
                    </Field>
                  </div>
                </section>
              )}

              <section className="border border-white/10 rounded-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((value) => !value)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-black/20 text-white"
                >
                  <span className="font-medium">Advanced JSON</span>
                  <ChevronDown size={16} className={`transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                </button>
                {advancedOpen && (
                  <div className="p-4 space-y-3">
                    <p className="text-xs text-gray-500">Optional developer view. Invalid JSON cannot be saved.</p>
                    <Textarea
                      value={sectionsText}
                      onChange={(e) => setSectionsText(e.target.value)}
                      className="min-h-[320px] font-mono text-xs bg-black/30 border-white/15 text-white"
                      spellCheck={false}
                    />
                    {jsonError && <p className="text-sm text-red-300">{jsonError}</p>}
                    <Button type="button" variant="outline" className="border-white/20 text-white" onClick={applyJsonToForm}>
                      Apply JSON to form
                    </Button>
                  </div>
                )}
              </section>

              <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:justify-end">
                <Button variant="outline" className="border-white/20 text-white" onClick={archivePage} disabled={!form.id}>
                  <Archive size={16} className="mr-2" />
                  Archive
                </Button>
                <Button variant="outline" className="border-white/20 text-white" onClick={saveDraft} disabled={saving}>
                  <Save size={16} className="mr-2" />
                  Save draft
                </Button>
                <Button className="bg-primary hover:bg-primary/90" onClick={publish} disabled={saving}>
                  <Send size={16} className="mr-2" />
                  Publish
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentManagement;
